import { useQuery } from "@tanstack/react-query";
import { EMPLOYEE_MANAGEMENT_SERVICE, restApiRequest } from "src/utils/Api";

/**
 * Item shape returned by employee-management
 * `GET /employee-accounts?employeeId=...`. Only the field used for
 * bank-visibility filtering is typed here.
 */
type EmployeeAccount = {
  pointsBankId: string;
};

/**
 * Fetches the set of points-bank ids the current user is allowed to see for
 * the given employee.
 *
 * The employee-management `/employee-accounts` endpoint applies the
 * server-side AHR bank-visibility filter (AccessValidator): OMB / technical
 * users get every bank, while an AHR only gets the banks assigned to them
 * (their `administeredPointsBanks`). We use the returned bank ids purely as an
 * allow-list to filter the richer account data coming from the points-banks
 * service, restoring the pre-JIT-981 behaviour.
 */
export const fetchAllowedBankIds = async (
  employeeId: string
): Promise<Set<string>> => {
  const response: EmployeeAccount[] = await restApiRequest(
    EMPLOYEE_MANAGEMENT_SERVICE,
    `/employee-accounts?employeeId=${encodeURIComponent(employeeId)}`,
    "GET",
    {},
    []
  );

  return new Set((response ?? []).map((account) => account.pointsBankId));
};

const useAllowedBankIds = (employeeId: string, active: boolean) =>
  useQuery<Set<string>, Error>({
    queryKey: ["charge-ups-allowed-bank-ids", employeeId],
    queryFn: () => fetchAllowedBankIds(employeeId),
    enabled: active && !!employeeId,
  });

export default useAllowedBankIds;
